/**
 * Cache respons API di browser (stale-while-revalidate).
 *
 * Reload halaman: data terakhir langsung tampil dari sessionStorage (0 ms), lalu
 * versi terbaru diambil di belakang dan menggantikannya. Pengguna tidak melihat
 * spinner untuk data yang sudah pernah dibuka di tab ini.
 *
 * - Key = host (tenant) + URL lengkap → tidak tercampur antar tenant/periode.
 * - sessionStorage: hilang saat tab ditutup; dihapus juga saat logout (clearApiCache).
 * - Hanya respons 2xx yang disimpan. Kuota penuh → entri lama dibuang, tidak pernah error.
 */
const NS = "avio:swr:v1:";
const MAX_AGE_MS = 6 * 3600e3;

type Entry = { at: number; data: unknown };

const keyOf = (url: string) => `${NS}${location.host}:${url}`;

export function readCache<T = any>(url: string): T | null {
  try {
    const raw = sessionStorage.getItem(keyOf(url));
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry;
    if (Date.now() - e.at > MAX_AGE_MS) return null;
    return e.data as T;
  } catch { return null; }
}

function writeCache(url: string, data: unknown) {
  const v = JSON.stringify({ at: Date.now(), data } satisfies Entry);
  try { sessionStorage.setItem(keyOf(url), v); }
  catch {
    // kuota penuh: buang separuh entri tertua lalu coba sekali lagi
    try {
      const ks = Object.keys(sessionStorage).filter((k) => k.startsWith(NS));
      const aged = ks.map((k) => { try { return [k, (JSON.parse(sessionStorage.getItem(k)!) as Entry).at] as const; } catch { return [k, 0] as const; } })
        .sort((a, b) => a[1] - b[1]);
      aged.slice(0, Math.ceil(aged.length / 2)).forEach(([k]) => sessionStorage.removeItem(k));
      sessionStorage.setItem(keyOf(url), v);
    } catch { /* abaikan */ }
  }
}

/** Simpan respons yang diambil manual (halaman yang perlu menangani 401/error sendiri). */
export function writeCacheExt(url: string, data: unknown) { if (data !== null && data !== undefined) writeCache(url, data); }

/** Ambil JSON: panggil onData(cache) segera bila ada, lalu onData(terbaru) setelah fetch. */
export function swrJson<T = any>(url: string, onData: (d: T | null) => void): Promise<T | null> {
  const cached = readCache<T>(url);
  if (cached !== null) onData(cached);
  return fetch(url)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { if (d !== null) { writeCache(url, d); onData(d); } else if (cached === null) onData(null); return d; })
    .catch(() => { if (cached === null) onData(null); return null; });
}

export function clearApiCache() {
  try { Object.keys(sessionStorage).filter((k) => k.startsWith(NS)).forEach((k) => sessionStorage.removeItem(k)); } catch { /* abaikan */ }
}
